/**
 * Register taxonomy — 3-character labels combining connectedness,
 * preparedness, and social differential.
 *
 * See Frontier requirements doc §3.1 Column G, and AGENT.md §5 glossary.
 * The register picker UI in apps/desktop builds labels from this taxonomy;
 * server validates labels against it.
 */

export const Connectedness = {
  i: { letter: 'i', name: 'Impersonal', description: 'Definitions/explanations; no specific readership. Third person only (e.g., a contract).' },
  n: { letter: 'n', name: 'Non-directional', description: 'Created with a readership/audience in mind but does not address them specifically (e.g., a recounted story).' },
  u: { letter: 'u', name: 'Uni-directional', description: 'Addresses a readership/audience that cannot respond or is asked to refrain (e.g., a transcribed TED Talk).' },
  m: { letter: 'm', name: 'Multi-directional', description: 'Addresses an audience that can respond (e.g., SMS, DM, conversation).' },
} as const;

export const Preparedness = {
  r: { letter: 'r', name: 'Reactive (spontaneous)', description: 'Immediate production, by need or by choice.' },
  i: { letter: 'i', name: 'Improvised (coached)', description: 'Appears spontaneous but after general training or coaching (e.g., trained spokespeople).' },
  e: { letter: 'e', name: 'Rehearsed (extemporaneous)', description: 'Live but with carefully crafted, rehearsed overall structure (e.g., 20-min presentations from notes).' },
  s: { letter: 's', name: 'Scripted (declaimed)', description: 'May or may not be live; fully scripted (e.g., teleprompter speeches).' },
  c: { letter: 'c', name: 'Composed (frozen)', description: 'Completely offline; iteratively reviewed and edited (e.g., the text of a novel).' },
} as const;

export const SocialDifferential = {
  k: { letter: 'k', name: 'Equal (known)', description: 'Known peers; very informal or colloquial attitude.' },
  a: { letter: 'a', name: 'Equal (assumed)', description: 'Assumed peers; casual/informal but likely not very colloquial.' },
  h: { letter: 'h', name: 'Higher-to-lower', description: 'Addressees considered at lower social level (e.g., arrogance, position of authority).' },
  l: { letter: 'l', name: 'Lower-to-higher', description: 'Addressees considered at higher social level (e.g., deference, respect, admiration).' },
} as const;

export type ConnectednessKey = keyof typeof Connectedness;
export type PreparednessKey = keyof typeof Preparedness;
export type SocialDifferentialKey = keyof typeof SocialDifferential;

const CONNECTEDNESS_KEYS = Object.keys(Connectedness) as ConnectednessKey[];
const PREPAREDNESS_KEYS = Object.keys(Preparedness) as PreparednessKey[];
const SOCIAL_DIFFERENTIAL_KEYS = Object.keys(SocialDifferential) as SocialDifferentialKey[];

export interface RegisterTriple {
  connectedness: ConnectednessKey;
  preparedness: PreparednessKey;
  socialDifferential: SocialDifferentialKey;
}

/**
 * Build a 3-character register label from a triple.
 * E.g. { connectedness: 'i', preparedness: 'c', socialDifferential: 'a' } -> 'ica'
 */
export const encodeRegister = (triple: RegisterTriple): string =>
  `${triple.connectedness}${triple.preparedness}${triple.socialDifferential}`;

/**
 * Parse a 3-character register label back into a triple.
 * Returns undefined if the label is invalid (not 3 chars, or any
 * character is not a known taxonomy key).
 */
export const decodeRegister = (label: string): RegisterTriple | undefined => {
  if (label.length !== 3) return undefined;
  const [c, p, s] = label;
  if (!c || !p || !s) return undefined;
  if (!(c in Connectedness)) return undefined;
  if (!(p in Preparedness)) return undefined;
  if (!(s in SocialDifferential)) return undefined;
  return {
    connectedness: c as ConnectednessKey,
    preparedness: p as PreparednessKey,
    socialDifferential: s as SocialDifferentialKey,
  };
};

export const isValidRegisterLabel = (label: string): boolean =>
  decodeRegister(label) !== undefined;

/**
 * Enumerate all possible register labels — useful for tests and UI filter
 * options.
 */
export const allRegisterLabels = (): string[] => {
  const out: string[] = [];
  for (const c of CONNECTEDNESS_KEYS)
    for (const p of PREPAREDNESS_KEYS)
      for (const s of SOCIAL_DIFFERENTIAL_KEYS)
        out.push(`${c}${p}${s}`);
  return out;
};
